import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { useToast } from '@/hooks/use-toast';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'lead' | 'listing' | 'system';
  read: boolean;
  created_at: string;
  data?: Record<string, any>;
}

export function useNotifications() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    if (!user) return;

    // Subscribe to real-time changes for new leads
    const leadsSubscription = supabase
      .channel('broker-leads')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'leads',
          filter: `listing_id=in.(select id from listings where broker_id=eq.${user.id})`
        },
        (payload) => {
          console.log('New lead received:', payload);
          
          // Create notification for new lead
          const notification: Notification = {
            id: `lead-${payload.new.id}`,
            title: 'New Lead Received!',
            message: `You have a new inquiry from ${payload.new.buyer_name || 'Anonymous'} for one of your listings.`,
            type: 'lead',
            read: false,
            created_at: new Date().toISOString(),
            data: { leadId: payload.new.id, listingId: payload.new.listing_id }
          };

          setNotifications(prev => [notification, ...prev]);
          setUnreadCount(prev => prev + 1);

          // Show toast notification
          toast({
            title: "New Lead!",
            description: notification.message,
          });
        }
      )
      .subscribe();

    // Subscribe to listing status changes
    const listingsSubscription = supabase
      .channel('broker-listings')
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'listings',
          filter: `broker_id=eq.${user.id}`
        },
        (payload) => {
          console.log('Listing updated:', payload);
          
          if (payload.old.status !== payload.new.status) {
            const notification: Notification = {
              id: `listing-${payload.new.id}-${Date.now()}`,
              title: 'Listing Status Changed',
              message: `Your listing "${payload.new.title}" status changed from ${payload.old.status} to ${payload.new.status}.`,
              type: 'listing',
              read: false,
              created_at: new Date().toISOString(),
              data: { listingId: payload.new.id }
            };

            setNotifications(prev => [notification, ...prev]);
            setUnreadCount(prev => prev + 1);
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(leadsSubscription);
      supabase.removeChannel(listingsSubscription);
    };
  }, [user, toast]);

  const markAsRead = (notificationId: string) => {
    setNotifications(prev => 
      prev.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  const markAllAsRead = () => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const clearNotification = (notificationId: string) => {
    setNotifications(prev => prev.filter(n => n.id !== notificationId));
    const notification = notifications.find(n => n.id === notificationId);
    if (notification && !notification.read) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }
  };

  return {
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotification
  };
}